import { useState, useRef } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Upload, FileSpreadsheet, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable 3-step import dialog: upload → preview → summary.
 *
 * Props:
 *  open, onOpenChange       – Dialog control
 *  entityLabel              – "Artikel" | "Rezepturen"
 *  previewEndpoint          – e.g. "/items/import/preview"
 *  commitEndpoint           – e.g. "/items/import/commit"
 *  templateUrl              – URL to download template (absolute, includes /api)
 *  renderPreviewRow         – (row) => JSX  — renders one row in the preview table
 *  previewKey               – "rows" | "groups" — property in preview response
 *  onDone                   – callback after commit (e.g. reload list)
 */
export default function ImportDialog({
  open, onOpenChange,
  entityLabel,
  previewEndpoint, commitEndpoint, templateUrl,
  renderPreviewHeader, renderPreviewRow, previewKey = "rows",
  onDone,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setSummary(null); };

  const close = () => { reset(); onOpenChange(false); };

  const runPreview = async (f) => {
    if (!f) return;
    setLoading(true); setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(previewEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vorschau fehlgeschlagen");
      setPreview(null);
    } finally { setLoading(false); }
  };

  const runCommit = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(commitEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSummary(data);
      toast.success(`Import: ${data.created} neu, ${data.updated} aktualisiert, ${data.skipped} übersprungen`);
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const rows = preview?.[previewKey] || [];
  const stats = preview?.stats;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-import">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Upload size={22} /> {entityLabel} importieren
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Upload */}
        {!preview && !summary && (
          <div className="space-y-4">
            <div className="rounded-xl border-2 border-dashed border-[var(--border)] p-8 text-center">
              <FileSpreadsheet size={40} className="mx-auto text-[var(--pop-caramel)] mb-2" />
              <p className="font-semibold mb-1">CSV oder Excel hochladen</p>
              <p className="text-xs text-[var(--text-muted)] mb-3">
                Unterstützt: .csv, .xlsx — max. 5 MB
              </p>
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm"
                ref={fileInputRef}
                className="hidden"
                data-testid="input-import-file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile(f); runPreview(f); }
                }}
              />
              <Button className="rounded-full bg-[var(--pop-caramel)] text-white hover:opacity-90 font-bold"
                      data-testid="btn-choose-file"
                      onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} className="mr-1" /> Datei auswählen
              </Button>
            </div>
            <div className="text-center">
              <a href={templateUrl}
                 target="_blank"
                 rel="noreferrer"
                 className="text-sm text-[var(--pop-pink)] font-semibold hover:underline inline-flex items-center gap-1"
                 data-testid="link-download-template">
                <FileSpreadsheet size={14} /> Excel-Vorlage herunterladen
              </a>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-6 text-[var(--text-muted)]">Verarbeite…</div>
        )}

        {/* Step 2: Preview */}
        {preview && !summary && !loading && (
          <div className="space-y-4">
            {/* Header-Mapping Diff (falls Backend liefert) */}
            {preview.header_info && (preview.header_info.mapped?.length > 0 || preview.header_info.unmatched?.length > 0) && (
              <div className="rounded-xl border border-[var(--border)] bg-[var(--pop-cream)]/30 p-3" data-testid="import-header-diff">
                <div className="overline text-xs mb-2 flex items-center gap-1">
                  <FileSpreadsheet size={12} /> Spalten-Zuordnung
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="font-semibold text-emerald-700 mb-1">
                      ✓ Erkannt ({preview.header_info.mapped?.length || 0})
                    </div>
                    <ul className="space-y-0.5">
                      {(preview.header_info.mapped || []).map((m, i) => (
                        <li key={i} className="flex items-center gap-2 font-mono" data-testid={`header-mapped-${m.field}`}>
                          <span className="pop-pill bg-emerald-100 text-emerald-800 text-[10px] font-bold">{m.field}</span>
                          <span className="text-[var(--text-muted)]">←</span>
                          <span title={`Alias: ${m.alias}`}>"{m.raw}"</span>
                          {m.normalized && (
                            <span className="pop-pill bg-amber-100 text-amber-800 text-[9px] font-bold"
                                  title="Header wurde automatisch bereinigt (Leerzeichen, BOM, Groß-/Kleinschreibung)">
                              normalisiert
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    {preview.header_info.unmatched?.length > 0 && (
                      <>
                        <div className="font-semibold text-amber-700 mb-1">
                          ⚠ Ignoriert ({preview.header_info.unmatched.length})
                        </div>
                        <ul className="space-y-0.5">
                          {preview.header_info.unmatched.map((h, i) => (
                            <li key={i} className="font-mono text-[var(--text-muted)]" data-testid={`header-unmatched-${i}`}>
                              &ldquo;{h}&rdquo;
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-4 gap-2 text-sm" data-testid="import-preview-stats">
              <div className="rounded-xl border border-[var(--border)] p-3 text-center">
                <div className="overline text-[10px]">Gesamt</div>
                <div className="text-xl font-bold font-mono">{stats?.total}</div>
              </div>
              <div className="rounded-xl border-2 border-green-200 bg-green-50 p-3 text-center">
                <div className="overline text-[10px] text-green-700">Neu anlegen</div>
                <div className="text-xl font-bold font-mono text-green-700" data-testid="stat-create">{stats?.create}</div>
              </div>
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-3 text-center">
                <div className="overline text-[10px] text-blue-700">Aktualisieren</div>
                <div className="text-xl font-bold font-mono text-blue-700" data-testid="stat-update">{stats?.update}</div>
              </div>
              <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-center">
                <div className="overline text-[10px] text-red-700">Überspringen</div>
                <div className="text-xl font-bold font-mono text-red-700" data-testid="stat-skip">{stats?.skip}</div>
              </div>
            </div>
            <div className="border border-[var(--border)] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[var(--pop-cream)]/40">
                  <tr>{renderPreviewHeader()}</tr>
                </thead>
                <tbody data-testid="import-preview-body">
                  {rows.slice(0, 200).map((row, i) => renderPreviewRow(row, i))}
                </tbody>
              </table>
              {rows.length > 200 && (
                <div className="p-2 text-center text-xs text-[var(--text-muted)] bg-[var(--pop-cream)]/20">
                  … und {rows.length - 200} weitere Zeilen
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset} className="rounded-full">Abbrechen</Button>
              <Button onClick={runCommit} data-testid="btn-commit-import"
                      disabled={loading || !stats?.total || (stats.create + stats.update === 0)}
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                <Check size={14} className="mr-1" />
                {stats?.create + stats?.update} Datensätze importieren
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Summary */}
        {summary && (
          <div className="space-y-4" data-testid="import-summary">
            <div className="rounded-xl border-2 border-green-200 bg-green-50 p-4 text-center">
              <Check size={36} className="mx-auto text-green-600 mb-2" />
              <div className="font-bold text-lg">Import abgeschlossen</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl border-2 border-green-200 bg-green-50 p-3 text-center">
                <div className="overline text-[10px] text-green-700">Neu angelegt</div>
                <div className="text-2xl font-bold font-mono text-green-700" data-testid="summary-created">{summary.created}</div>
              </div>
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-3 text-center">
                <div className="overline text-[10px] text-blue-700">Aktualisiert</div>
                <div className="text-2xl font-bold font-mono text-blue-700" data-testid="summary-updated">{summary.updated}</div>
              </div>
              <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-center">
                <div className="overline text-[10px] text-red-700">Übersprungen</div>
                <div className="text-2xl font-bold font-mono text-red-700" data-testid="summary-skipped">{summary.skipped}</div>
              </div>
            </div>
            {summary.errors?.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 max-h-40 overflow-y-auto" data-testid="summary-errors">
                <div className="overline text-xs mb-1 flex items-center gap-1 text-red-700">
                  <AlertTriangle size={12} /> Fehler ({summary.errors.length})
                </div>
                <ul className="text-xs space-y-1">
                  {summary.errors.slice(0, 30).map((e, i) => (
                    <li key={i}>Zeile {e.row}: <span className="text-red-700">{e.reason}</span></li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button onClick={close} className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                      data-testid="btn-close-import">
                <X size={14} className="mr-1" /> Schließen
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
